import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X, Instagram, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { buttonStyles } from '../lib/buttonStyles';
import Logo from './Logo';
import { track } from '../lib/analytics';

import { Link, useLocation } from 'react-router-dom';

const SEVIYE_TESTI_URL = 'https://sinav.uz/seviye-testi';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    const trackSeviyeTestiClick = () => track('view_item', { item_id: 'seviye_testi', item_type: 'exam' });

    const isHomePage = pathname === '/';

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 96;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            setIsOpen(false);
        }
    };

    return (
        <>
            {/* Yüzen cam pill navbar — mobbin.com'dan getComputedStyle ile ölçülen
                gerçek değerler: bg rgba(237,237,237,.64), backdrop-filter blur(48px),
                border yok, shadow yok, tam stadium radius, scroll'da HİÇ değişmiyor. */}
            <header
                className="fixed left-1/2 top-4 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-6xl rounded-full py-4 backdrop-blur-[48px] bg-[rgba(237,237,237,0.64)] dark:bg-[rgba(20,20,20,0.64)]"
            >
                <div className="flex items-center justify-between px-5 md:px-7">
                    {/* Logo */}
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="flex items-center gap-2.5 text-dark dark:text-white"
                    >
                        <Logo size={32} showWordmark={false} />
                        <span className="text-xl font-heading font-semibold tracking-tight">{t('common.brand')}</span>
                    </a>

                    {/* Desktop Nav */}
                    {isHomePage ? (
                        <nav className="hidden md:flex items-center gap-6 text-dark dark:text-white text-sm">
                            <button onClick={() => scrollToSection('hero')} className="font-semibold hover:text-primary transition-colors">{t('navbar.nav1')}</button>
                            <button onClick={() => scrollToSection('features')} className="font-semibold hover:text-primary transition-colors">{t('navbar.nav2')}</button>
                            <button onClick={() => scrollToSection('powerup')} className="font-semibold hover:text-primary transition-colors">{t('navbar.nav5')}</button>
                            <button onClick={() => scrollToSection('pricing')} className="font-semibold hover:text-primary transition-colors">{t('navbar.nav3')}</button>
                            <button onClick={() => scrollToSection('contact')} className="font-semibold hover:text-primary transition-colors">{t('navbar.nav4')}</button>
                            <a href={SEVIYE_TESTI_URL} target="_blank" rel="noopener noreferrer" onClick={trackSeviyeTestiClick} className="font-semibold hover:text-primary transition-colors">
                                {t('navbar.nav7')}
                            </a>
                        </nav>
                    ) : (
                        <nav className="hidden md:flex items-center gap-6 text-dark dark:text-white text-sm">
                            <Link to="/" className="font-semibold hover:text-primary transition-colors flex items-center gap-1">
                                &larr; {t('navbar.nav1')}
                            </Link>
                            <a href={SEVIYE_TESTI_URL} target="_blank" rel="noopener noreferrer" onClick={trackSeviyeTestiClick} className="font-semibold hover:text-primary transition-colors">
                                {t('navbar.nav7')}
                            </a>
                        </nav>
                    )}

                    {/* Actions */}
                    <div className="hidden md:flex items-center gap-2.5 relative">
                        <a
                            href="https://sinav.uz"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={buttonStyles({ variant: 'outline', size: 'sm', className: 'text-primary hover:bg-primary/10' })}
                            title={i18n.language === 'tr' ? 'Konuşma Sınavı' : 'Imtihon markazi'}
                        >
                            {i18n.language === 'tr' ? 'Konuşma Sınavı' : 'Imtihon markazi'}
                        </a>

                        {isHomePage && (
                            <>
                                <a
                                    href="https://www.instagram.com/turkdunyasi2026/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full border flex items-center justify-center transition-all border-primary/40 text-primary hover:bg-primary/10"
                                    title="Instagram"
                                >
                                    <Instagram size={16} />
                                </a>
                                <a
                                    href="https://t.me/turkdunyasi_on"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full border flex items-center justify-center transition-all border-secondary/40 text-secondary hover:bg-secondary/10"
                                    title="Telegram"
                                >
                                    <Send size={16} />
                                </a>
                            </>
                        )}

                        <button
                            onClick={() => i18n.changeLanguage(i18n.language === 'tr' ? 'uz' : 'tr')}
                            className="w-9 h-9 rounded-full border flex items-center justify-center transition-all overflow-hidden border-primary/40 hover:bg-primary/10"
                            title={i18n.language === 'tr' ? "Tilni o'zgartirish" : "Dil değiştir"}
                        >
                            {i18n.language === 'tr' ? (
                                <span className="text-lg leading-none" role="img" aria-label="TR">🇹🇷</span>
                            ) : (
                                <span className="text-lg leading-none" role="img" aria-label="UZ">🇺🇿</span>
                            )}
                        </button>

                        <motion.button
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            onClick={() => {
                                if (document.documentElement.classList.contains('dark')) {
                                    document.documentElement.classList.remove('dark');
                                    localStorage.theme = 'light';
                                } else {
                                    document.documentElement.classList.add('dark');
                                    localStorage.theme = 'dark';
                                }
                            }}
                            className="w-9 h-9 rounded-full border border-primary/40 text-primary hover:bg-primary/10 flex items-center justify-center transition-all text-base"
                            title="Mavzuni o'zgartirish"
                        >
                            <span className="block dark:hidden">🌙</span>
                            <span className="hidden dark:block">☀️</span>
                        </motion.button>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label={isOpen ? "Menüyü kapat" : "Menüyü aç"}
                        aria-expanded={isOpen}
                        className="md:hidden p-2 rounded-lg transition-colors text-dark dark:text-white hover:bg-black/5 dark:hover:bg-white/10"
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </header>

            {/* Mobil menü — pill navbar'ın dışında, tam genişliğe yakın ayrı bir kart */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="fixed top-[76px] left-3 right-3 z-40 md:hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-3xl p-5 flex flex-col gap-4 shadow-xl"
                    >
                        <a
                            href="https://sinav.uz"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={buttonStyles({ variant: 'primary', size: 'lg', fullWidth: true })}
                        >
                            {i18n.language === 'tr' ? 'Konuşma Sınavı' : 'Imtihon markazi'}
                        </a>

                        {isHomePage ? (
                            <>
                                <button onClick={() => scrollToSection('hero')} className="block py-2 font-medium text-left dark:text-gray-200">{t('navbar.nav1')}</button>
                                <button onClick={() => scrollToSection('features')} className="block py-2 font-medium text-left dark:text-gray-200">{t('navbar.nav2')}</button>
                                <button onClick={() => scrollToSection('powerup')} className="block py-2 font-medium text-left dark:text-gray-200">{t('navbar.nav5')}</button>
                                <button onClick={() => scrollToSection('pricing')} className="block py-2 font-medium text-left dark:text-gray-200">{t('navbar.nav3')}</button>
                                <button onClick={() => scrollToSection('contact')} className="block py-2 font-medium text-left dark:text-gray-200">{t('navbar.nav4')}</button>

                                <div className="h-[1px] w-full bg-gray-100 dark:bg-gray-800 my-1"></div>
                                <a href={SEVIYE_TESTI_URL} target="_blank" rel="noopener noreferrer" onClick={() => { trackSeviyeTestiClick(); setIsOpen(false); }} className="py-2 font-medium text-left text-primary pl-2 border-l-2 border-primary">
                                    {t('navbar.nav7')}
                                </a>
                            </>
                        ) : (
                            <>
                                <Link to="/" onClick={() => setIsOpen(false)} className="block py-2 font-medium text-left dark:text-gray-200">&larr; {t('navbar.nav1')}</Link>
                                <div className="h-[1px] w-full bg-gray-100 dark:bg-gray-800 my-1"></div>
                                <a href={SEVIYE_TESTI_URL} target="_blank" rel="noopener noreferrer" onClick={() => { trackSeviyeTestiClick(); setIsOpen(false); }} className="py-2 font-medium text-left text-primary pl-2 border-l-2 border-primary flex items-center gap-2">
                                    {t('navbar.nav7')}
                                </a>
                            </>
                        )}

                        <div className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-800 mt-2">
                            <span className="text-sm text-gray-500">{t('navbar.dark_mode')}</span>
                            <button
                                onClick={() => {
                                    document.documentElement.classList.toggle('dark');
                                    localStorage.theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
                                }}
                                className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full"
                            >
                                <span className="dark:hidden">🌙</span>
                                <span className="hidden dark:block">☀️</span>
                            </button>
                        </div>

                        {isHomePage && (
                            <div className="flex items-center gap-4 py-4 border-y border-gray-100 dark:border-gray-800 my-2">
                                <a
                                    href="https://www.instagram.com/turkdunyasi2026/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-primary/10 text-primary font-bold"
                                >
                                    <Instagram size={20} /> Instagram
                                </a>
                                <a
                                    href="https://t.me/turkdunyasi_on"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-secondary/10 text-secondary font-bold"
                                >
                                    <Send size={20} /> Telegram
                                </a>
                            </div>
                        )}

                        <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 mb-2">
                            <span className="text-sm text-gray-500">{t('navbar.language')}</span>
                            <button
                                onClick={() => i18n.changeLanguage(i18n.language === 'tr' ? 'uz' : 'tr')}
                                className="font-bold flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-primary/20 bg-primary/5 text-primary"
                            >
                                {i18n.language === 'tr' ? 'TURKCHA 🇹🇷' : 'O\'ZBEKCHA 🇺🇿'}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
